import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Ban, ChevronDown, ChevronRight, ChevronUp, Loader2, MoreHorizontal, RotateCcw } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Progress } from '@/components/ui/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { RelativeTime, SectionCard, formatNumber } from '../../../components/design-system/admin';
import { JobStatusBadge } from './JobStatusBadge';
import type { QueueBatchSummary, QueueJob, QueueType } from './queue-monitor.types';
import { getProgressPercent, isBatchRunning, jobsOfBatch } from './queue-monitor.utils';

interface QueueBatchesTableProps {
    batches: QueueBatchSummary[];
    jobs: QueueJob[];
    queue: QueueType;
    /** The whole section open or folded. */
    expanded: boolean;
    onToggleExpanded: () => void;
    /** The batch whose jobs are shown under its row. */
    expandedBatch: string | null;
    onToggleBatch: (batchId: string) => void;
    cancellingBatch: string | null;
    onRetryFailed: (batchId: string) => void;
    onCancelBatch: (batchId: string) => void;
}

/** A count cell: quiet at zero, red for failures. */
const Count = ({ value, danger }: { value: number; danger?: boolean }) => (
    <span className={cn('tabular-nums', !value ? 'text-muted-foreground' : danger && 'text-destructive')}>
        {formatNumber(value)}
    </span>
);

/**
 * Recent import batches (bulk) or pattern groups (seamless): how far each got,
 * its jobs on demand, and retry / cancel.
 */
export function QueueBatchesTable({
    batches,
    jobs,
    queue,
    expanded,
    onToggleExpanded,
    expandedBatch,
    onToggleBatch,
    cancellingBatch,
    onRetryFailed,
    onCancelBatch,
}: QueueBatchesTableProps) {
    const { t } = useTranslation();
    const seamless = queue === 'seamless';

    return (
        <SectionCard
            title={seamless ? t('ADMIN_QUEUE.BATCHES.TITLE_PATTERNS') : t('ADMIN_QUEUE.BATCHES.TITLE')}
            description={
                seamless
                    ? t('ADMIN_QUEUE.BATCHES.COUNT_PATTERNS', { count: batches.length })
                    : t('ADMIN_QUEUE.BATCHES.COUNT', { count: batches.length })
            }
            action={
                <Button variant="ghost" size="sm" onClick={onToggleExpanded} aria-expanded={expanded}>
                    {expanded ? t('ADMIN_QUEUE.BATCHES.HIDE') : t('ADMIN_QUEUE.BATCHES.SHOW')}
                    {expanded ? <ChevronUp /> : <ChevronDown />}
                </Button>
            }
            flush={expanded}
            contentClassName={expanded ? undefined : 'hidden'}
            testId="queue-batches"
        >
            {expanded && (
                <div className="border-t">
                    <Table>
                        <TableHeader>
                            <TableRow className="hover:bg-transparent">
                                <TableHead className="w-10 ps-4" />
                                <TableHead>{seamless ? t('ADMIN_QUEUE.BATCHES.COL_PATTERN') : t('ADMIN_QUEUE.BATCHES.COL_BATCH')}</TableHead>
                                <TableHead className="min-w-40">{t('ADMIN_QUEUE.BATCHES.COL_PROGRESS')}</TableHead>
                                <TableHead className="text-end">{t('ADMIN_QUEUE.BATCHES.COL_WAITING')}</TableHead>
                                <TableHead className="text-end">{t('ADMIN_QUEUE.BATCHES.COL_ACTIVE')}</TableHead>
                                <TableHead className="text-end">{t('ADMIN_QUEUE.BATCHES.COL_COMPLETED')}</TableHead>
                                <TableHead className="text-end">{t('ADMIN_QUEUE.BATCHES.COL_FAILED')}</TableHead>
                                <TableHead>{t('ADMIN_QUEUE.BATCHES.COL_CREATED')}</TableHead>
                                <TableHead className="w-12 pe-4">
                                    <span className="sr-only">{t('ADMIN_SHELL.COMMON.ACTIONS')}</span>
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {batches.map((batch) => {
                                const id = String(batch._id ?? '');
                                const open = expandedBatch === id;
                                const progress = getProgressPercent(batch);
                                const running = isBatchRunning(batch);
                                const cancelling = cancellingBatch === id;
                                const batchJobs = open ? jobsOfBatch(jobs, id, queue) : [];
                                return (
                                    <Fragment key={id}>
                                        <TableRow data-testid="queue-batch-row" className={cn(open && 'bg-muted/50')}>
                                            <TableCell className="ps-4">
                                                <Button
                                                    variant="ghost"
                                                    size="icon-xs"
                                                    aria-expanded={open}
                                                    aria-label={open ? t('ADMIN_QUEUE.BATCHES.HIDE_JOBS') : t('ADMIN_QUEUE.BATCHES.SHOW_JOBS')}
                                                    onClick={() => onToggleBatch(id)}
                                                >
                                                    {open ? <ChevronDown /> : <ChevronRight className="rtl:rotate-180" />}
                                                </Button>
                                            </TableCell>
                                            <TableCell>
                                                <span className="font-mono text-xs" title={id}>
                                                    {seamless || id.length <= 8 ? id : `${id.slice(0, 8)}…`}
                                                </span>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-3">
                                                    <Progress
                                                        value={progress}
                                                        aria-label={t('ADMIN_QUEUE.BATCHES.COL_PROGRESS')}
                                                        className={cn(
                                                            'h-1.5 flex-1',
                                                            batch.failed > 0
                                                                ? '[&_[data-slot=progress-indicator]]:bg-destructive'
                                                                : '[&_[data-slot=progress-indicator]]:bg-foreground/70',
                                                        )}
                                                    />
                                                    <span className="w-10 text-end text-xs text-muted-foreground tabular-nums">
                                                        {Math.round(progress)}%
                                                    </span>
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-end">
                                                <Count value={batch.pending} />
                                            </TableCell>
                                            <TableCell className="text-end">
                                                <Count value={(batch.processing || 0) + (batch.generating_seamless || 0)} />
                                            </TableCell>
                                            <TableCell className="text-end">
                                                <Count value={batch.completed} />
                                            </TableCell>
                                            <TableCell className="text-end">
                                                <Count value={batch.failed} danger />
                                            </TableCell>
                                            <TableCell className="text-muted-foreground">
                                                <RelativeTime value={batch.createdAt} />
                                            </TableCell>
                                            <TableCell className="pe-4 text-end">
                                                {(running || batch.failed > 0) && (
                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger asChild>
                                                            <Button
                                                                variant="ghost"
                                                                size="icon-sm"
                                                                aria-label={t('ADMIN_QUEUE.BATCHES.ROW_ACTIONS')}
                                                                disabled={cancelling}
                                                            >
                                                                {cancelling ? <Loader2 className="animate-spin" /> : <MoreHorizontal />}
                                                            </Button>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent align="end" className="w-auto">
                                                            {batch.failed > 0 && (
                                                                <DropdownMenuItem onSelect={() => onRetryFailed(id)}>
                                                                    <RotateCcw />
                                                                    {t('ADMIN_QUEUE.BATCHES.RETRY_FAILED')}
                                                                </DropdownMenuItem>
                                                            )}
                                                            {running && (
                                                                <DropdownMenuItem variant="destructive" onSelect={() => onCancelBatch(id)}>
                                                                    <Ban />
                                                                    {t('ADMIN_QUEUE.BATCHES.CANCEL')}
                                                                </DropdownMenuItem>
                                                            )}
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                        {open && (
                                            <TableRow className="hover:bg-transparent" data-testid="queue-batch-jobs">
                                                <TableCell colSpan={9} className="bg-muted/30 p-0">
                                                    <Table>
                                                        <TableHeader>
                                                            <TableRow className="hover:bg-transparent">
                                                                <TableHead className="ps-14 text-muted-foreground">{t('ADMIN_QUEUE.JOBS.COL_NAME')}</TableHead>
                                                                <TableHead className="text-muted-foreground">{t('ADMIN_QUEUE.JOBS.COL_OPERATION')}</TableHead>
                                                                <TableHead className="text-muted-foreground">{t('ADMIN_QUEUE.JOBS.COL_STATUS')}</TableHead>
                                                                <TableHead className="text-muted-foreground">{t('ADMIN_QUEUE.JOBS.COL_ERROR')}</TableHead>
                                                                <TableHead className="pe-4 text-muted-foreground">{t('ADMIN_QUEUE.JOBS.COL_CREATED')}</TableHead>
                                                            </TableRow>
                                                        </TableHeader>
                                                        <TableBody>
                                                            {batchJobs.length === 0 ? (
                                                                <TableRow className="hover:bg-transparent">
                                                                    <TableCell colSpan={5} className="py-4 text-center text-muted-foreground">
                                                                        {t('ADMIN_QUEUE.BATCHES.NO_JOBS')}
                                                                    </TableCell>
                                                                </TableRow>
                                                            ) : (
                                                                batchJobs.map((job) => (
                                                                    <TableRow key={job._id} className="hover:bg-transparent">
                                                                        <TableCell className="ps-14">{job.jobName}</TableCell>
                                                                        <TableCell className="text-muted-foreground capitalize">{job.operation}</TableCell>
                                                                        <TableCell>
                                                                            <JobStatusBadge status={job.status} />
                                                                        </TableCell>
                                                                        <TableCell
                                                                            className="max-w-[240px] truncate text-destructive"
                                                                            title={job.errorDetails?.userMessage || job.error}
                                                                        >
                                                                            {job.error || job.errorDetails?.userMessage || (
                                                                                <span className="text-muted-foreground">—</span>
                                                                            )}
                                                                        </TableCell>
                                                                        <TableCell className="pe-4 text-muted-foreground">
                                                                            <RelativeTime value={job.createdAt} />
                                                                        </TableCell>
                                                                    </TableRow>
                                                                ))
                                                            )}
                                                        </TableBody>
                                                    </Table>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </Fragment>
                                );
                            })}
                        </TableBody>
                    </Table>
                </div>
            )}
        </SectionCard>
    );
}
